<template>
  <div class="step-content">
    <h5 class="mb-3">
      <i class="feather icon-link mr-2"></i>
      Unidades Vinculadas
    </h5>
    <p class="text-muted mb-4">
      Informe os dados das unidades vinculadas à entidade principal para análise do credenciamento.
    </p>

    <!-- Informações Importantes -->
    <b-alert variant="info" show class="mb-4">
      <div class="alert-body">
        <h6 class="alert-heading">
          <i class="feather icon-info mr-1"></i>
          Informações Importantes
        </h6>
        <hr>
        <ul class="mb-0">
          <li><strong>Unidades Vinculadas:</strong> Instituições que fazem parte do mesmo grupo ou organização</li>
          <li><strong>Documentação:</strong> Anexe os atos de credenciamento ou autorização que comprovem a vinculação, bem como o regimento interno ou estatuto.</li>
          <li><strong>Localização:</strong> Informe o município e UF onde a unidade está fisicamente localizada</li>
        </ul>
      </div>
    </b-alert>

    <!-- Formulário de Nova Unidade -->
    <b-card v-if="!disabled" class="mb-4">
      <template #header>
        <h6 class="mb-0">
          <i class="feather icon-plus-circle text-primary mr-2"></i>
          Adicionar Nova Unidade
        </h6>
      </template>

      <!-- Nome da Instituição -->
      <b-row>
        <b-col md="12">
          <b-form-group label="Nome da Instituição" label-for="institutionName">
            <b-form-input
              id="institutionName"
              v-model="currentInstitution.institution_name"
              placeholder="Digite o nome da instituição vinculada"
              :state="getFieldState('institution_name')"
              :disabled="disabled"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- CNPJ da Instituição -->
      <b-row>
        <b-col md="6">
          <b-form-group label="CNPJ da Instituição" label-for="institutionCnpj">
            <b-form-input
              id="institutionCnpj"
              v-model="currentInstitution.institution_cnpj"
              v-mask="'##.###.###/####-##'"
              placeholder="00.000.000/0000-00"
              :state="getFieldState('institution_cnpj')"
              :disabled="disabled"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Localização -->
      <b-row>
        <b-col md="5">
          <b-form-group label="UF" label-for="institutionState">
            <v-select
              id="institutionState"
              v-model="currentInstitution.state"
              :options="federationUnitList"
              label="name"
              @input="selectFederationUnit"
              :state="getFieldState('state')"
              :disabled="disabled"
              placeholder="Selecione o estado"
            >
              <div slot="no-options">Nenhum registro encontrado!</div>
            </v-select>
          </b-form-group>
        </b-col>
        <b-col md="7">
          <b-form-group label="Município" label-for="institutionCity">
            <v-select
              id="institutionCity"
              v-model="currentInstitution.city"
              :options="cityList"
              label="name"
              :disabled="disabled || !currentInstitution.state"
              :state="getFieldState('city')"
              placeholder="Selecione o município"
            >
              <div slot="no-options">Nenhum registro encontrado!</div>
            </v-select>
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Upload de Documentos -->
      <h6 class="mt-4 mb-3 text-muted">Documentação</h6>

      <!-- Atos de Credenciamento/Autorização -->
      <b-row>
        <b-col md="12">
          <b-form-group label="Atos de Credenciamento/Autorização">
            <b-form-file
              v-model="currentInstitution.institution_authorization"
              placeholder="Clique para selecionar o arquivo ou arraste aqui..."
              drop-placeholder="Solte o arquivo aqui..."
              accept="application/pdf"
              :state="getFieldState('institution_authorization')"
              :disabled="disabled"
            />
            <small class="form-text text-muted">
              <i class="feather icon-file-text mr-1"></i>
              Apenas arquivos PDF. Tamanho máximo: 10MB
            </small>
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Regimento Interno ou Estatuto -->
      <b-row>
        <b-col md="12">
          <b-form-group label="Regimento Interno ou Estatuto">
            <b-form-file
              v-model="currentInstitution.school_regulations"
              placeholder="Clique para selecionar o arquivo ou arraste aqui..."
              drop-placeholder="Solte o arquivo aqui..."
              accept="application/pdf"
              :state="getFieldState('school_regulations')"
              :disabled="disabled"
            />
            <small class="form-text text-muted">
              <i class="feather icon-file-text mr-1"></i>
              Apenas arquivos PDF. Tamanho máximo: 10MB
            </small>
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Botão de Adicionar -->
      <div v-if="!disabled" class="text-right mt-4">
        <b-button
          variant="primary"
          @click="addInstitution"
          :disabled="!isFormValid"
          class="btn-dynamic-primary"
        >
          <i class="feather icon-plus mr-1"></i>
          Adicionar Unidade
        </b-button>
      </div>
    </b-card>

    <!-- Lista de Unidades Vinculadas -->
    <div v-if="linkedInstitutions.length > 0" class="linked-institutions-list">
      <b-card>
        <template #header>
          <h6 class="mb-0">
            <i class="feather icon-list text-primary mr-2"></i>
            Unidades Vinculadas Adicionadas
          </h6>
        </template>
        <b-list-group>
          <b-list-group-item v-for="(institution, index) in linkedInstitutions" :key="index" class="d-flex justify-content-between align-items-start">
            <div>
              <div class="d-flex align-items-center mb-1">
                <i class="feather icon-building text-primary mr-2"></i>
                <strong>{{ institution.institution_name }}</strong>
              </div>
              <div class="text-muted small">
                CNPJ: {{ institution.institution_cnpj }} |
                {{ institution.city_name ? institution.city_name : '' }}/{{ institution.state_short ? institution.state_short : '' }}
              </div>
              <!-- Debug Info -->
              <div v-if="$attrs.debug" class="text-warning small mt-1">
                <strong>DEBUG:</strong>
                Auth: {{ !!institution.institution_authorization ? 'Sim' : 'Não' }} |
                School: {{ !!institution.school_regulations ? 'Sim' : 'Não' }}
              </div>
              <div v-if="institution.institution_authorization" class="d-flex align-items-center mt-1">
                <span class="text-muted small">
                  <i class="feather icon-file-text mr-1"></i>
                  <a v-if="getAuthorizationDownloadUrl(institution.institution_authorization)" :href="getAuthorizationDownloadUrl(institution.institution_authorization)" target="_blank" class="mr-1 download-link text-primary" title="Baixar arquivo de autorização">
                    <i class="feather icon-download"></i>
                  </a>
                  Atos de Credenciamento/Autorização
                  <span v-if="isFileObject(institution.institution_authorization)" class="ml-2">
                    {{ institution.institution_authorization.name }}
                  </span>
                  <span v-else-if="institution.institution_authorization.file_name" class="ml-2">
                    {{ institution.institution_authorization.file_name }}
                  </span>
                  <span v-else-if="institution.institution_authorization.name" class="ml-2">
                    {{ institution.institution_authorization.name }}
                  </span>
                </span>
              </div>
              <div v-if="institution.school_regulations" class="d-flex align-items-center mt-1">
                <span class="text-muted small">
                  <i class="feather icon-file-text mr-1"></i>
                  <a v-if="getSchoolRegulationsDownloadUrl(institution.school_regulations)" :href="getSchoolRegulationsDownloadUrl(institution.school_regulations)" target="_blank" class="mr-1 download-link text-primary" title="Baixar regimento interno ou estatuto">
                    <i class="feather icon-download"></i>
                  </a>
                  Regimento Interno ou Estatuto
                  <span v-if="isFileObject(institution.school_regulations)" class="ml-2">
                    {{ institution.school_regulations.name }}
                  </span>
                  <span v-else-if="institution.school_regulations.file_name" class="ml-2">
                    {{ institution.school_regulations.file_name }}
                  </span>
                  <span v-else-if="institution.school_regulations.name" class="ml-2">
                    {{ institution.school_regulations.name }}
                  </span>
                </span>
              </div>
            </div>
            <b-button
              v-if="!disabled"
              variant="outline-danger"
              size="sm"
              @click="removeInstitution(institution)"
            >
              <i class="feather icon-trash-2"></i>
            </b-button>
          </b-list-group-item>
        </b-list-group>
      </b-card>
    </div>

    <!-- Modal de Confirmação de Remoção -->
    <b-modal
      v-model="showRemoveConfirmModal"
      title="Confirmar Remoção"
      centered
      hide-footer
      @hide="cancelRemove"
    >
      <div class="text-center">
        <i class="feather icon-trash-2 text-danger mb-2" style="font-size: 2rem;"></i>
        <p>
          Tem certeza que deseja remover esta unidade vinculada?
        </p>
      </div>
      <div class="text-center mt-3">
        <b-button variant="danger" class="mr-2" @click="confirmRemove">
          Remover
        </b-button>
        <b-button variant="secondary" @click="cancelRemove">
          Cancelar
        </b-button>
      </div>
    </b-modal>

    <!-- Modal de Confirmação de Continuação -->
    <b-modal
      v-model="showConfirmModal"
      title="Confirmar Continuação"
      centered
      hide-footer
      @hide="$emit('confirm-proceed', false)"
    >
      <div class="text-center">
        <i class="feather icon-alert-triangle text-warning mb-2" style="font-size: 2rem;"></i>
        <p v-if="confirmationType === 'no-units'">
          Você não adicionou nenhuma unidade vinculada. Deseja continuar mesmo assim?
        </p>
        <p v-else-if="confirmationType === 'partial-fields'">
          Você tem campos parcialmente preenchidos. Deseja descartar essas informações e continuar?
        </p>
      </div>
      <div class="text-center mt-3">
        <b-button variant="primary" class="mr-2" @click="$emit('confirm-proceed', true)">
          Confirmar
        </b-button>
        <b-button variant="secondary" @click="$emit('confirm-proceed', false)">
          Cancelar
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BFormFile,
  BFormInvalidFeedback,
  BAlert,
  BCard,
  BButton,
  BListGroup,
  BListGroupItem,
  BModal,
  BFormSelect,
  BFormSelectOption
} from 'bootstrap-vue'
import { mask } from 'vue-the-mask'
import FederationUnitService from '@/services/api/FederationUnitService'
import CityService from '@/services/api/CityService'
import vSelect from 'vue-select'

export default {
  name: 'LinkedInstitutionsStep',
  directives: {
    mask
  },
  components: {
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormFile,
    BFormInvalidFeedback,
    BAlert,
    BCard,
    BButton,
    BListGroup,
    BListGroupItem,
    BModal,
    BFormSelect,
    BFormSelectOption,
    vSelect
  },
  props: {
    formData: {
      type: Array,
      required: true,
      default: () => []
    },
    errors: {
      type: Object,
      default: () => ({})
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      services: {
        federationUnitService: null,
        cityService: null
      },
      currentInstitution: {
        institution_id: null,
        institution_name: '',
        institution_cnpj: '',
        city: null,
        state: null,
        institution_authorization: null,
        school_regulations: null
      },
      linkedInstitutions: [],
      showConfirmModal: false,
      showRemoveConfirmModal: false,
      confirmationType: null,
      institutionToRemove: null,
      federationUnitList: [],
      cityList: [],
      showAddModal: false,
      showDeleteModal: false,
      editingInstitution: null,
      institutionToDelete: null,
      form: {
        institution_id: null,
        institution_name: '',
        institution_cnpj: '',
        city_id: null,
        state_id: null,
        institution_authorization: null,
        school_regulations: null
      },
      formErrors: {},
      isReadOnly: false
    }
  },
  computed: {
    isFormValid() {
      return (
        this.currentInstitution.institution_name &&
        this.currentInstitution.institution_cnpj &&
        this.currentInstitution.city &&
        this.currentInstitution.state &&
        this.currentInstitution.institution_authorization &&
        this.currentInstitution.school_regulations
      )
    },

    hasInstitutions() {
      return this.linkedInstitutions.length > 0
    },

    showCityList() {
      return this.cityList.length > 0
    }
  },
  watch: {
    formData: {
      immediate: true,
      handler(newValue) {
        console.log('LinkedInstitutionsStep - formData changed:', newValue);

        if (newValue && Array.isArray(newValue)) {
          console.log('LinkedInstitutionsStep - Processando array de instituições');
          this.linkedInstitutions = newValue.map(institution => {
            console.log('LinkedInstitutionsStep - Instituição original:', institution);
            const mappedInstitution = {
              institution_id: institution.institution_id,
              institution_name: institution.institution_name,
              institution_cnpj: institution.institution_cnpj,
              city_id: institution.city_id,
              city_name: institution.city_name,
              state_id: institution.state_id,
              state_name: institution.state_name,
              state_short: institution.state_short,
              institution_authorization: institution.institution_authorization,
              school_regulations: institution.school_regulations
            };
            console.log('LinkedInstitutionsStep - Instituição mapeada:', mappedInstitution);
            return mappedInstitution;
          });
          console.log('LinkedInstitutionsStep - Lista final de instituições:', this.linkedInstitutions);
        } else {
          console.log('LinkedInstitutionsStep - Dados inválidos ou vazios:', newValue);
          this.linkedInstitutions = [];
        }
      }
    }
  },
  created() {
    console.log('LinkedInstitutionsStep - Componente criado');
    console.log('LinkedInstitutionsStep - formData inicial:', this.formData);
  },
  methods: {
    getFieldState(fieldName) {
      return this.errors[fieldName] ? 'invalid' : null
    },
    getFieldMessage(fieldName) {
      return this.errors[fieldName] || ''
    },
    resetCurrentInstitution() {
      this.currentInstitution = {
        institution_id: null,
        institution_name: '',
        institution_cnpj: '',
        city: null,
        state: null,
        institution_authorization: null,
        school_regulations: null
      }
      this.cityList = []
    },
    selectFederationUnit(federationUnit) {
      // Permite seleção mesmo quando desabilitado, pois pode ser necessário para visualização
      // Mas não altera dados se desabilitado
      if (this.disabled) {
        console.log('🔒 Formulário desabilitado - seleção de UF bloqueada');
        return;
      }

      console.log('Selected federation unit:', federationUnit)
      this.currentInstitution.state = federationUnit
      this.currentInstitution.city = null
      this.cityList = []
      if (federationUnit) {
        this.$vs.loading()
        this.services.cityService
          .findAllByFederationUnity(federationUnit.id)
          .then(
            data => {
              console.log('Cities loaded:', data)
              this.cityList = data
              this.$vs.loading.close()
            },
            error => {
              console.error('Error loading cities:', error)
              this.$vs.loading.close()
              this.$vs.notify({
                title: 'Erro',
                text: 'Erro ao carregar cidades',
                color: 'danger'
              })
            }
          )
      }
    },
    addInstitution() {
      console.log('LinkedInstitutionsStep - INICIANDO addInstitution');
      console.log('LinkedInstitutionsStep - currentInstitution:', this.currentInstitution);
      console.log('LinkedInstitutionsStep - isFormValid:', this.isFormValid);

      const institutionData = {
        institution_name: this.currentInstitution.institution_name,
        institution_cnpj: this.currentInstitution.institution_cnpj,
        city_id: this.currentInstitution.city ? this.currentInstitution.city.id : null,
        city_name: this.currentInstitution.city ? this.currentInstitution.city.name : '',
        state_id: this.currentInstitution.state ? this.currentInstitution.state.id : null,
        state_name: this.currentInstitution.state ? this.currentInstitution.state.name : '',
        state_short: this.currentInstitution.state ? this.currentInstitution.state.short : '',
        institution_authorization: this.currentInstitution.institution_authorization,
        school_regulations: this.currentInstitution.school_regulations
      };

      console.log('LinkedInstitutionsStep - Dados a serem enviados:', institutionData);
      console.log('LinkedInstitutionsStep - Emitindo evento add-institution');

      this.$emit('add-institution', institutionData);

      console.log('LinkedInstitutionsStep - Evento emitido, resetando formulário');
      this.resetCurrentInstitution();
    },
    async removeInstitution(institution) {
      try {
        this.institutionToRemove = institution;
        this.showRemoveConfirmModal = true;
      } catch (error) {
        console.error('Error removing institution:', error);
      }
    },
    async confirmRemove() {
      try {
        if (!this.institutionToRemove) return;

        // Emit event to parent to handle API call and removal
        await this.$emit('remove-institution', this.institutionToRemove);

        // Close modal and reset state
        this.showRemoveConfirmModal = false;
        this.institutionToRemove = null;
      } catch (error) {
        console.error('Error removing institution:', error);
      }
    },
    cancelRemove() {
      this.showRemoveConfirmModal = false;
      this.institutionToRemove = null;
    },
    // Método para validar o step antes de prosseguir
    validateStep() {
      return new Promise((resolve) => {
        // Se não há unidades cadastradas, mostra modal de confirmação
        if (this.linkedInstitutions.length === 0) {
          this.confirmationType = 'no-units';
          this.showConfirmModal = true;
          this.$once('confirm-proceed', (confirmed) => {
            this.showConfirmModal = false;
            resolve(confirmed);
          });
          return;
        }
        // Se há campos parcialmente preenchidos, mostra modal de confirmação
        const hasPartialFields = Object.values(this.currentInstitution).some(value => value !== null && value !== '');
        if (hasPartialFields) {
          this.confirmationType = 'partial-fields';
          this.showConfirmModal = true;
          this.$once('confirm-proceed', (confirmed) => {
            this.showConfirmModal = false;
            if (confirmed) this.resetCurrentInstitution();
            resolve(confirmed);
          });
          return;
        }
        // Caso contrário, pode prosseguir
        resolve(true);
      });
    },
    editInstitution(institution) {
      this.editingInstitution = institution;
      this.form = {
        institution_id: institution.institution_id,
        institution_name: institution.institution_name,
        institution_cnpj: institution.institution_cnpj,
        city_id: institution.city_id,
        state_id: institution.state_id,
        institution_authorization: institution.institution_authorization,
        school_regulations: institution.school_regulations
      };
      this.showAddModal = true;
    },
    confirmDelete(institution) {
      this.institutionToDelete = institution;
      this.showDeleteModal = true;
    },
    deleteInstitution() {
      this.$emit('remove-institution', this.institutionToDelete);
      this.showDeleteModal = false;
      this.institutionToDelete = null;
    },
    resetForm() {
      this.editingInstitution = null;
      this.form = {
        institution_id: null,
        institution_name: '',
        institution_cnpj: '',
        city_id: null,
        state_id: null,
        institution_authorization: null,
        school_regulations: null
      };
      this.showAddModal = false;
    },

    isFileObject(obj) {
      // Verificação mais robusta para detectar objetos File
      return obj && typeof obj === 'object' &&
             typeof obj.size === 'number' &&
             typeof obj.type === 'string' &&
             typeof obj.name === 'string' &&
             typeof obj.lastModified === 'number';
    },

    getAuthorizationDownloadUrl(authorization) {
      if (!authorization) return null;

      console.log('getAuthorizationDownloadUrl - authorization:', authorization);

      // Se é um arquivo File do navegador (recém selecionado)
      if (this.isFileObject(authorization)) {
        return URL.createObjectURL(authorization);
      }

      // Se é um objeto media retornado do servidor
      if (authorization.original_url) {
        return authorization.original_url;
      }

      // Se é um arquivo que foi enviado (tem url_tmp)
      if (authorization.url_tmp) {
        return authorization.url_tmp;
      }

      // Se é um arquivo que foi carregado do servidor (tem metadata)
      if (authorization.metadata && authorization.metadata.url_tmp) {
        return authorization.metadata.url_tmp;
      }

      // Verificar outras possíveis propriedades de URL
      if (authorization.url_temp) {
        return authorization.url_temp;
      }

      if (authorization.url) {
        return authorization.url;
      }

      if (authorization.download_url) {
        return authorization.download_url;
      }

      return null;
    },

    getSchoolRegulationsDownloadUrl(schoolRegulations) {
      if (!schoolRegulations) return null;

      console.log('getSchoolRegulationsDownloadUrl - schoolRegulations:', schoolRegulations);

      // Se é um arquivo File do navegador (recém selecionado)
      if (this.isFileObject(schoolRegulations)) {
        return URL.createObjectURL(schoolRegulations);
      }

      // Se é um objeto media retornado do servidor
      if (schoolRegulations.original_url) {
        return schoolRegulations.original_url;
      }

      // Se é um arquivo que foi enviado (tem url_tmp)
      if (schoolRegulations.url_tmp) {
        return schoolRegulations.url_tmp;
      }

      // Se é um arquivo que foi carregado do servidor (tem metadata)
      if (schoolRegulations.metadata && schoolRegulations.metadata.url_tmp) {
        return schoolRegulations.metadata.url_tmp;
      }

      // Verificar outras possíveis propriedades de URL
      if (schoolRegulations.url_temp) {
        return schoolRegulations.url_temp;
      }

      if (schoolRegulations.url) {
        return schoolRegulations.url;
      }

      if (schoolRegulations.download_url) {
        return schoolRegulations.download_url;
      }

      return null;
    }
  },
  mounted() {
    // Load federation units
    this.$vs.loading()
    this.services.federationUnitService = FederationUnitService.build(this.$vs)
    this.services.cityService = CityService.build(this.$vs)

    this.services.federationUnitService.all().then(
      data => {
        this.federationUnitList = data
        this.$vs.loading.close()
      },
      error => {
        this.$vs.loading.close()
      }
    )
  },
  beforeDestroy() {
    // Limpa URLs de objeto para evitar vazamentos de memória
    this.linkedInstitutions.forEach(institution => {
      if (this.isFileObject(institution.institution_authorization)) {
        const url = this.getAuthorizationDownloadUrl(institution.institution_authorization);
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      }
      if (this.isFileObject(institution.school_regulations)) {
        const url = this.getSchoolRegulationsDownloadUrl(institution.school_regulations);
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      }
    });
  }
}
</script>

<style lang="scss" scoped>
.step-content {
  padding: 1rem 0;
}

/* Estilos para botões com cores dinâmicas */
.btn-dynamic-primary {
  background-color: var(--dynamic-primary) !important;
  border-color: var(--dynamic-primary) !important;
  color: #fff !important;

  &:hover,
  &:focus {
    background-color: var(--dynamic-primary-dark) !important;
    border-color: var(--dynamic-primary-dark) !important;
  }

  &:disabled {
    background-color: var(--dynamic-primary-light) !important;
    border-color: var(--dynamic-primary-light) !important;
    opacity: 0.65;
  }
}

.btn-outline-dynamic-primary {
  color: var(--dynamic-primary) !important;
  border-color: var(--dynamic-primary) !important;
  background-color: transparent !important;

  &:hover,
  &:focus {
    color: #fff !important;
    background-color: var(--dynamic-primary) !important;
    border-color: var(--dynamic-primary) !important;
  }

  &:disabled {
    color: var(--dynamic-primary-light) !important;
    border-color: var(--dynamic-primary-light) !important;
    opacity: 0.65;
  }
}

/* Estilos para os links de download */
.download-link {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  transition: opacity 0.2s ease;
  vertical-align: middle;

  &:hover {
    opacity: 0.7;
    text-decoration: none;
  }

  i {
    font-size: 14px;
  }
}

/* Melhorar alinhamento dos itens da lista */
.list-group-item {
  .d-flex.align-items-center {
    align-items: center;
  }

  .feather {
    flex-shrink: 0;
  }
}
</style>
